const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

export class EncryptionService {
  private static async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      passwordKey,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async generateSalt(): Promise<Uint8Array> {
    return crypto.getRandomValues(new Uint8Array(16));
  }

  static async generateIV(): Promise<Uint8Array> {
    return crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  }

  static async encrypt(
    data: string,
    password: string
  ): Promise<{ encrypted: string; metadata: { salt: string; iv: string } }> {
    const encoder = new TextEncoder();
    const salt = await this.generateSalt();
    const iv = await this.generateIV();
    const key = await this.deriveKey(password, salt);

    const encryptedData = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      encoder.encode(data)
    );

    return {
      encrypted: this.arrayBufferToBase64(encryptedData),
      metadata: {
        salt: this.arrayBufferToBase64(salt),
        iv: this.arrayBufferToBase64(iv),
      },
    };
  }

  static async decrypt(
    encryptedData: string,
    password: string,
    metadata: { salt: string; iv: string }
  ): Promise<string> {
    const salt = this.base64ToArrayBuffer(metadata.salt);
    const iv = this.base64ToArrayBuffer(metadata.iv);
    const key = await this.deriveKey(password, new Uint8Array(salt));

    const decryptedData = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: new Uint8Array(iv) },
      key,
      this.base64ToArrayBuffer(encryptedData)
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  }

  static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  static async encryptJSON(data: any, password: string) {
    const jsonString = JSON.stringify(data);
    return this.encrypt(jsonString, password);
  }

  static async decryptJSON(encryptedData: string, password: string, metadata: { salt: string; iv: string }) {
    const decrypted = await this.decrypt(encryptedData, password, metadata);
    return JSON.parse(decrypted);
  }
}
